import { Alert, DatePicker, Form, Input, Modal } from 'antd';
import dayjs from 'dayjs';
import type { WorkOrderMove } from '../work-orders.api';

export type InputMove = Extract<WorkOrderMove, 'reject' | 'cancel' | 'schedule'>;

const COPY: Record<InputMove, { title: string; okText: string }> = {
  reject: { title: 'Reject work order', okText: 'Reject' },
  cancel: { title: 'Cancel work order', okText: 'Cancel work order' },
  schedule: { title: 'Schedule work order', okText: 'Schedule' },
};

interface WorkOrderMoveModalProps {
  move: InputMove | null;
  submitting: boolean;
  error: string | null;
  onSubmit: (move: InputMove, body: Record<string, string>) => Promise<boolean>;
  onClose: () => void;
}

/** The moves that need an answer first: a date to schedule for, or the reason the raiser and the record will read. */
export function WorkOrderMoveModal({ move, submitting, error, onSubmit, onClose }: WorkOrderMoveModalProps) {
  const [form] = Form.useForm<{ reason?: string; scheduledFor?: dayjs.Dayjs }>();
  if (!move) return null;
  const copy = COPY[move];

  async function submit(values: { reason?: string; scheduledFor?: dayjs.Dayjs }) {
    const body: Record<string, string> =
      move === 'schedule' ? { scheduledFor: (values.scheduledFor as dayjs.Dayjs).format('YYYY-MM-DD') } : { reason: (values.reason ?? '').trim() };
    if (!(await onSubmit(move as InputMove, body))) return;
    form.resetFields();
    onClose();
  }

  return (
    <Modal
      open
      title={copy.title}
      okText={copy.okText}
      cancelText="Back"
      confirmLoading={submitting}
      onOk={() => form.submit()}
      onCancel={onClose}
      destroyOnHidden
    >
      {error && <Alert className="mb-4" type="error" title={error} showIcon />}
      <Form form={form} layout="vertical" requiredMark={false} onFinish={submit}>
        {move === 'schedule' ? (
          <Form.Item name="scheduledFor" label="Scheduled for" rules={[{ required: true, message: 'Choose the date' }]}>
            <DatePicker className="w-full" />
          </Form.Item>
        ) : (
          <Form.Item name="reason" label="Reason" rules={[{ required: true, whitespace: true, message: 'Give a reason' }, { max: 500 }]}>
            <Input.TextArea rows={3} />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
}
